import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { pageVisibilityAPI } from '../services/api';
import { formatApiError } from '../utils/errorMessage';
import ErrorBoundary from './ErrorBoundary';

/**
 * Page Visibility Modal Component
 * Allows users to view and manage page visibility for themselves and subordinates
 */
const PageVisibilityModal = ({ isOpen, onClose, targetUserId = null, targetUserName = null }) => {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [subordinates, setSubordinates] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(targetUserId);
  const [selectedUserName, setSelectedUserName] = useState(targetUserName);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadSubordinates();
      if (selectedUserId && selectedUserId !== 'self' && selectedUserId !== null) {
        loadUserPages(selectedUserId);
      } else {
        loadMyPages();
      }
    } else {
      // Reset state when modal closes
      setPages([]);
      setError('');
      setSuccess('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedUserId]);

  const loadSubordinates = async () => {
    try {
      const response = await pageVisibilityAPI.getSubordinates();
      setSubordinates(response.data || []);
    } catch (error) {
      console.error('Failed to load subordinates:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const loadMyPages = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await pageVisibilityAPI.getMyPages();
      setPages(response.data || []);
      setSelectedUserId('self');
      setSelectedUserName('Myself');
    } catch (error) {
      setError(formatApiError(error, 'Failed to load pages'));
    } finally {
      setLoading(false);
    }
  };

  const loadUserPages = async (userId) => {
    setLoading(true);
    setError('');
    try {
      const response = await pageVisibilityAPI.getUserPages(userId);
      setPages(response.data || []);
      
      // Find user name
      const user = subordinates.find(u => u.user_id === userId || u.id === userId);
      if (user) {
        setSelectedUserName(`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email);
      }
    } catch (error) {
      setError(formatApiError(error, 'Failed to load user pages'));
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (userId, userName) => {
    if (userId === 'self') {
      loadMyPages();
    } else {
      setSelectedUserId(userId);
      loadUserPages(userId);
    }
  };

  const handlePageToggle = (pageKey) => {
    setPages(prevPages =>
      prevPages.map(page =>
        page.page_key === pageKey
          ? { ...page, is_visible: !page.is_visible }
          : page
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Determine target user ID - use 'self' if none selected or 'self' is selected
      const targetUserId = selectedUserId && selectedUserId !== 'self' ? selectedUserId : 'self';
      
      await pageVisibilityAPI.updateUserPages(targetUserId, pages.map(p => ({
        page_key: p.page_key,
        is_visible: p.is_visible
      })));
      
      setSuccess('Page visibility updated successfully');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setError(formatApiError(error, 'Failed to update page visibility'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Group pages by category
  const groupedPages = (pages || []).reduce((acc, page) => {
    if (!page || !page.page_key) return acc;
    const category = page.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(page);
    return acc;
  }, {});

  return (
    <ErrorBoundary section="Page Visibility Modal">
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Page Visibility</h2>
              <p className="text-sm text-gray-600 mt-1">
                Manage which pages are visible {selectedUserId ? `to ${selectedUserName}` : 'to you'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Any page you hide will always remain listed here so you can switch it back on later.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* User Selector */}
          <div className="px-6 py-4 border-b bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select User:
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleUserSelect('self', 'Myself')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  !selectedUserId || selectedUserId === 'self'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Myself
              </button>
              {subordinates.map((user) => {
                const userId = user.user_id || user.id;
                const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
                return (
                  <button
                    key={userId}
                    onClick={() => handleUserSelect(userId, userName)}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      selectedUserId === userId
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {userName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-600">{success}</p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Loading pages...</p>
              </div>
            ) : pages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No pages available. Please check your connection and try again.</p>
                {error && (
                  <p className="text-sm text-red-600 mt-2">{error}</p>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedPages).length > 0 ? (
                  Object.entries(groupedPages).map(([category, categoryPages]) => (
                    <div key={category}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 capitalize">
                        {category.replace('_', ' ')}
                      </h3>
                      <div className="space-y-2">
                        {categoryPages.map((page) => (
                          <label
                            key={page.page_key}
                            className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={page.is_visible === true}
                              onChange={() => handlePageToggle(page.page_key)}
                              className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            />
                            <div className="ml-3 flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {page.page_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {page.route_path}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No pages found.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default PageVisibilityModal;

